import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { intl } from "LocaleWrapper";

import { Card } from "@cofano/cck";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import DeleteIcon from "@mui/icons-material/Delete";

import CharterSelect, { CharterSelectOption } from "view/charters/components/selects/CharterSelect";
import { getCharterTypeColor } from "view/charters/utils/CharterUtils";
import { VoyageSchemaInput, VoyageSchemaOutput } from "view/voyages/utils/voyage.schema";

import { DossierLink } from "utils/LinkUtils";
import { getCharterIcon } from "utils/charterUtils";

import { CharterSubtype, CharterType } from "__generated__/enums";

const VoyagePurchaseChartersForm = () => {

	const { control, watch, setValue } = useFormContext<VoyageSchemaInput, unknown, VoyageSchemaOutput>();

	const { fields, append, remove } = useFieldArray({
		name: "purchaseCharters",
		control,
		keyName: "generatedId",
	});

	const [ selectedCharter, setSelectedCharter ] = useState<CharterSelectOption | null>(null);

	const [ start, end, ship ] = watch([ "start", "end", "ship" ]);

	const onDelete = (index: number) => {
		remove(index);

		if (fields.length === 0) {
			setValue("creditor", ship?.owner ?? null);
		}
	};

	const saveCharter = (charter: CharterSelectOption | null) => {
		setSelectedCharter(selectedCharter);

		if (charter) {
			append({
				...charter,
				id: charter.id,
				charterNumber: charter.charterNumber,
				creditor: charter.creditor ? { id: charter.creditor.id, name: charter.creditor.name } : null,
				debtor: charter.debtor ? { id: charter.debtor.id, name: charter.debtor.name } : null,
				validFrom: "validFrom" in charter ? charter.validFrom ?? null : null,
				validUntil: "validUntil" in charter ? charter.validUntil ?? null : null,
				shortNotice: false,
			});
		}

		setValue("creditor", charter?.creditor ?? null);

		setSelectedCharter(null);
	};

	return (
		<Grid container spacing={2}>
			<Grid size={12}>
				<Card title={intl.formatMessage({ id: "CHARTERS" })} icon={<AppsIcon />}>
					<Grid container spacing={2}>
						<Grid size={12}>
							<CharterSelect
								getOptionDisabled={(option) => fields.map(c => c.id).includes(option.id)}
								autoSelect={false}
								search={{
									creditors: [ ship?.operator?.id, ship?.owner?.id ],
									start: start ?? undefined,
									end: end ?? undefined,
									charterType: CharterType.Purchase,
									charterSubtypes: [ CharterSubtype.Spot, CharterSubtype.Time ],
									ship: ship ?? undefined,
								}}
								value={selectedCharter}
								onChange={(charter) => saveCharter(charter)}
								onCreate={(charter) => saveCharter(charter)}
							/>
						</Grid>

						<Grid size={12}>
							{fields.length > 0 ? (
								<Box p={2} pt={0}>
									{fields.map((charter, index) => (
										<Card
											key={charter.generatedId}
											collapsable={false}
											color={charter.type ? getCharterTypeColor(charter.type, charter.subtype) : undefined}
											title={
												<Box sx={{ display: "flex", alignItems: "center" }}>
													<Box marginRight={1}>
														{getCharterIcon(charter.type, charter.subtype, "1x")}
													</Box>
													<Typography component="span" fontSize={14} fontWeight="bold">
														{intl.formatMessage({ id: "CHARTER" })} #{index + 1}
													</Typography>
												</Box>
											}
											subtitle={(charter.id && charter.type) ? (
												<DossierLink
													charter={{
														id: charter.id,
														charterNumber: charter.charterNumber || "",
														type: charter.type,
													}} />
											) : intl.formatMessage({ id: "NEW" })}
											actions={[ {
												onClick: () => onDelete(index),
												icon: <DeleteIcon />,
											} ]}
										/>
									))}
								</Box>
							) : (
								<Box p={2} sx={{ display: "flex", justifyContent: "center" }}>
									<Typography
										component="span"
										fontStyle="italic"
										fontSize="small">
										{intl.formatMessage({ id: "VOYAGE_CHARTERS_NOT_LINKED" })}
									</Typography>
								</Box>
							)}
						</Grid>
					</Grid>
				</Card>
			</Grid>
		</Grid>
	);
};

export default VoyagePurchaseChartersForm;
